// PortalJobs: the signed-in job board on the CRM layout. Search and filters in
// one card, roles as card rows on the left, the role itself in a card on the
// right with Apply at its foot and Lexa's preparation card under it. Applying
// happens in-app via POST /api/public/apply/:ref; roles already applied to are
// marked; on a phone the role opens as a full sheet.
// P2-PORTAL (22 Oct 2026)
import { useState, useEffect, useCallback, useMemo } from 'react';
import API_BASE from '../../../config';
import { Ico, ICONS, workPattern, LexaPrepCard } from './portalKit';
import { filterRoles } from '../../../components/gtSearch'; // C1-SEARCH

const PAGE_SIZE = 10;
const PATTERNS = [
  { value: '', label: 'Any pattern' },
  { value: 'on-site', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
];

export default function PortalJobs({ getToken, onNeedCv, go, onChanged }) {
  const [state, setState] = useState('loading');
  const [roles, setRoles] = useState([]);
  const [appliedRefs, setAppliedRefs] = useState(new Set());
  const [selectedRef, setSelectedRef] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);
  const [q, setQ] = useState('');
  const [pattern, setPattern] = useState('');
  const [location, setLocation] = useState('');
  const [page, setPage] = useState(1);
  const [needCv, setNeedCv] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const token = await getToken();
      const [rolesRes, appsRes] = await Promise.all([
        fetch(API_BASE + '/api/public/roles'),
        fetch(API_BASE + '/api/portal/applications', { headers: { Authorization: 'Bearer ' + token } }),
      ]);
      if (!rolesRes.ok) { setState('error'); return; }
      const rolesData = await rolesRes.json();
      const list = Array.isArray(rolesData) ? rolesData : (rolesData.roles || []);
      let applied = new Set();
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        applied = new Set((appsData.applications || []).map((a) => a.role.ref));
      }
      setRoles(list);
      setAppliedRefs(applied);
      setState('ready');
      if (list.length && isWide()) setSelectedRef(list[0].ref);
    } catch (e) { setState('error'); }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const apply = useCallback(async (roleRef) => {
    setApplying(true); setApplyMsg(null);
    try {
      const token = await getToken();
      const r = await fetch(API_BASE + '/api/public/apply/' + encodeURIComponent(roleRef), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 403 && data.error === 'email_unverified') {
        setApplyMsg({ kind: 'error', text: 'Verify your email address, then apply again.' }); return;
      }
      if (r.status === 409 && data.error === 'cv_required') { setNeedCv(roleRef); setApplyMsg(null); return; }
      if (!r.ok) { setApplyMsg({ kind: 'error', text: 'Could not complete your application. Try again shortly.' }); return; }
      setAppliedRefs((prev) => new Set(prev).add(roleRef));
      setApplyMsg({
        kind: 'ok',
        text: data.already_applied ? 'You have already applied to this role.' : 'Application sent. You can follow it under Applications.',
      });
      if (onChanged) onChanged();
    } catch (e) {
      setApplyMsg({ kind: 'error', text: 'Could not complete your application. Try again shortly.' });
    } finally { setApplying(false); }
  }, [getToken, onChanged]);

  const locations = useMemo(() => {
    const seen = [];
    roles.forEach((r) => { if (r.public_location && seen.indexOf(r.public_location) < 0) seen.push(r.public_location); });
    return seen.sort();
  }, [roles]);

  const filtered = useMemo(() => {
    return filterRoles(roles, q).filter((r) => { // C1-SEARCH: every word, anywhere in the role
      if (pattern && normPattern(r.public_work_pattern) !== pattern) return false;
      if (location && r.public_location !== location) return false;
      return true;
    });
  }, [roles, q, pattern, location]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const selected = roles.find((r) => r.ref === selectedRef) || null;
  const choose = (ref) => { setSelectedRef(ref); setApplyMsg(null); setNeedCv(null); };

  if (state === 'loading') return <div className="gtr-card gtp2-state">Loading open roles</div>;
  if (state === 'error') return <div className="gtr-card gtp2-state">We could not load roles just now. Try again shortly.</div>;

  return (
    <div className="gtp2-jobs">
      <div className="gtr-card gtp2-toolbar">
        <label className="gtp2-search">
          <Ico d={ICONS.search} />
          <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search roles: title, skills, place…" autoComplete="off" aria-label="Search roles" />
        </label>
        <div className="gtp2-pills" role="group" aria-label="Filter by work pattern">
          {PATTERNS.map((p) => (
            <button key={p.value} type="button" aria-pressed={pattern === p.value}
              className={'gtp2-pill' + (pattern === p.value ? ' gtp2-pill--on' : '')}
              onClick={() => { setPattern(p.value); setPage(1); }}>{p.label}</button>
          ))}
        </div>
        {locations.length > 1 && (
          <div className="gtp2-pills" role="group" aria-label="Filter by location">
            <button type="button" aria-pressed={location === ''}
              className={'gtp2-pill' + (location === '' ? ' gtp2-pill--on' : '')}
              onClick={() => { setLocation(''); setPage(1); }}>Anywhere</button>
            {locations.map((l) => (
              <button key={l} type="button" aria-pressed={location === l}
                className={'gtp2-pill' + (location === l ? ' gtp2-pill--on' : '')}
                onClick={() => { setLocation(l); setPage(1); }}><Ico d={ICONS.pin} />{l}</button>
            ))}
          </div>
        )}
      </div>

      <div className="gtp2-split">
        <div className="gtp2-listcol">
          <div className="gtp2-count">{filtered.length} {filtered.length === 1 ? 'open role' : 'open roles'}</div>
          {slice.length === 0 ? (
            <div className="gtr-card gtp2-state">No roles match your search.</div>
          ) : (
            <div className="gtp2-rows">
              {slice.map((r) => {
                const applied = appliedRefs.has(r.ref);
                const chips = [r.public_location, workPattern(r.public_work_pattern)].filter(Boolean);
                return (
                  <button key={r.ref} type="button"
                    className={'gtp2-jrow' + (r.ref === selectedRef ? ' gtp2-jrow--sel' : '')}
                    aria-current={r.ref === selectedRef ? 'true' : undefined} onClick={() => choose(r.ref)}>
                    <span className="gtr2-listico"><Ico d={ICONS.brief} size={15} /></span>
                    <span className="gtp2-jrow-main">
                      <span className="gtp2-jrow-top">
                        <b>{r.title}</b>
                        {applied && <span className="gtp2-tag gtp2-tag--green">Applied</span>}
                      </span>
                      {r.public_salary_text && <span className="gtr2-rate">{r.public_salary_text}</span>}
                      {chips.length > 0 && (
                        <span className="gtp2-jrow-chips">{chips.map((c, i) => <span key={i} className="gtr2-chip">{c}</span>)}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {pages > 1 && (
            <div className="gtp2-pager">
              <button type="button" className="gtr2-btn gtr2-btn--sm" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>Previous</button>
              <span>Page {safePage} of {pages}</span>
              <button type="button" className="gtr2-btn gtr2-btn--sm" disabled={safePage === pages} onClick={() => setPage(safePage + 1)}>Next</button>
            </div>
          )}
        </div>

        <div className={'gtp2-detailcol' + (selected ? ' gtp2-detailcol--open' : '')}>
          {!selected ? (
            <div className="gtr-card gtp2-state">Choose a role to see the details.</div>
          ) : (
            <>
              <article className="gtr-card gtp2-role">
                <button type="button" className="gtr2-back gtp2-sheetback" onClick={() => setSelectedRef(null)}><Ico d={ICONS.back} />All roles</button>
                <div className="gtp2-role-top">
                  <div className="gtp2-role-id">
                    <h2 className="gtp2-role-title">{selected.title}</h2>
                    <div className="gtr2-chips">
                      {selected.public_location && <span className="gtr2-chip"><Ico d={ICONS.pin} />{selected.public_location}</span>}
                      {workPattern(selected.public_work_pattern) && <span className="gtr2-chip">{workPattern(selected.public_work_pattern)}</span>}
                      {domainLabel(selected.expertise_domain) && <span className="gtr2-chip gtr2-chip--domain"><Ico d={ICONS.spark} />{domainLabel(selected.expertise_domain)}</span>}
                    </div>
                  </div>
                  {selected.public_salary_text && <div className="gtp2-role-pay">{selected.public_salary_text}</div>}
                </div>

                {selected.client_blurb && <div className="gtp2-blurb">{selected.client_blurb}</div>}

                {selected.advert_html
                  ? <div className="gtp2-advert" dangerouslySetInnerHTML={{ __html: selected.advert_html }} />
                  : <p className="gtr2-muted">Full details for this role are being prepared.</p>}

                {needCv === selected.ref && (
                  <div className="gtpt-cvgate">
                    <div className="gtpt-cvgate-h">Your CV first</div>
                    <p className="gtpt-cvgate-p">We cannot put you forward on a name and an email alone. Add your CV to your profile, then come back and apply. It takes a minute.</p>
                    {onNeedCv && <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={onNeedCv}>Go to your profile</button>}
                  </div>
                )}
                {applyMsg && <p className={'gtp2-alert gtp2-alert--' + (applyMsg.kind === 'error' ? 'err' : 'ok')}>{applyMsg.text}</p>}

                <div className="gtp2-applybar">
                  {appliedRefs.has(selected.ref) ? (
                    <span className="gtp2-applied"><Ico d={ICONS.check} />You have applied to this role</span>
                  ) : (
                    <button type="button" className="gtr2-btn gtr2-btn--primary gtp2-applybtn" disabled={applying} onClick={() => apply(selected.ref)}>
                      {applying ? 'Sending your application' : 'Apply for this role'}
                    </button>
                  )}
                </div>
              </article>

              <LexaPrepCard roleTitle={selected.title}
                onPractise={() => go('Lexa', { kind: 'interview', role: selected.ref })}
                onCoach={() => go('Lexa', { kind: 'career', role: '' })} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function isWide() { try { return window.matchMedia('(min-width: 900px)').matches; } catch (e) { return true; } }
function normPattern(v) { return String(v || '').replace(/_/g, '-').toLowerCase(); }
function domainLabel(v) {
  if (!v || v === 'general') return '';
  return String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
